import { DataType } from "@/components/DataTable/Models";
import { IconTable } from "@codexteam/icons";
import { API, BlockAPI, BlockTool } from "@editorjs/editorjs";
import { BlockToolConstructorOptions, BlockToolData } from "@editorjs/editorjs/types/tools";
import { createRoot } from "react-dom/client";
import { DocumentationTableBlockComponent } from "./DocumentationTableBlockComponent";

export type DocumentationTableBlockData = BlockToolData & {
    title?: string;
    caption?: string;
    columns: DocumentationTableColumnData[];
};

export type DocumentationTableColumnData = {
    value: string;
    dataType: DataType;
};

export type DocumentationTableBlockConfig = {
    currentLanguage: string;
};

const DEFAULT_CONFIG: { [key: string]: DocumentationTableBlockData } = {
    "int-en": {
        title: "Documentation level",
        columns: [
            {
                value: "Document type",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nxx1",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx2",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx3",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx4",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx5",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-fr": {
        title: "NIVEAU DE DOCUMENTATION",
        columns: [
            {
                value: "Type de document",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nxx1",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx2",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx3",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx4",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx5",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-de": {
        title: "Documentation level",
        columns: [
            {
                value: "Document type",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nxx1",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx2",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx3",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx4",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx5",
                dataType: DataType.BOOLEAN,
            },
        ],
    },

    "int-nl": {
        title: "Documentatieniveau",
        columns: [
            {
                value: "Type document",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nxx1",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx2",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx3",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx4",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nxx5",
                dataType: DataType.BOOLEAN,
            },
        ],
    },
};

export class DocumentationTableBlockTool implements BlockTool {
    public static get toolbox() {
        return {
            title: "GID - Documentation",
            icon: IconTable,
        };
    }

    api: API;
    block?: BlockAPI;
    config: DocumentationTableBlockData;
    data: DocumentationTableBlockData;
    currentData: DocumentationTableBlockData;
    CSS: { [key: string]: string };
    nodes: { [key: string]: HTMLElement | null };

    constructor(params: BlockToolConstructorOptions<DocumentationTableBlockData, DocumentationTableBlockConfig>) {
        const { api, block, config, data } = params;
        if (!config) throw new Error("DocumentationTableBlockTool configuration missing");

        this.api = api;
        this.block = block;
        this.data = data;
        this.config = DEFAULT_CONFIG[config.currentLanguage]!;

        if (data && Object.keys(data).length > 0) {
            this.data = data;
        } else {
            this.data = JSON.parse(JSON.stringify(DEFAULT_CONFIG[config.currentLanguage]!));
        }

        this.currentData = JSON.parse(JSON.stringify(this.data));

        this.CSS = {
            wrapper: "template-editor-content-block",
        };

        this.nodes = {
            holder: null,
            table: null,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(block: HTMLElement) {
        return {
            title: this.api.sanitizer.clean(this.currentData.title || "", {}),
            columns: this.currentData.columns.map((column: DocumentationTableColumnData) => ({
                ...column,
                value: this.api.sanitizer.clean(column.value || "", {}),
            })),
            caption: this.api.sanitizer.clean(this.currentData.caption || "", {}),
            showGroupHeaderRows: true,
        };
    }

    render(): HTMLElement {
        if (!this.nodes.table) {
            const rootNode = document.createElement("div");
            rootNode.setAttribute("class", this.CSS.wrapper);
            this.nodes.table = rootNode;

            const root = createRoot(rootNode);
            root.render(
                <DocumentationTableBlockComponent
                    data={this.data}
                    defaults={this.config}
                    onChange={(data) => {
                        this.currentData = data;
                        this.block?.dispatchChange();
                    }}
                />
            );
        }

        if (!this.nodes.holder) {
            const holderNode = document.createElement("div");
            holderNode.setAttribute("class", this.CSS.wrapper);
            holderNode.appendChild(this.nodes.table);
            this.nodes.holder = holderNode;
        }

        return this.nodes.holder;
    }
}
